<template>
  <div class="ma-4 px-4">
    <v-sheet outlined rounded class="pa-4 mb-4 d-flex flex-wrap align-center">
      <div class="mr-auto">
        <div class="text-subtitle-1 font-weight-medium">Student Electives</div>
        <div class="text-caption text--secondary">
          Review each student's courses and manage their elective / stream
          selection.
        </div>
      </div>

      <div class="d-flex align-center flex-wrap">
        <v-chip small color="primary" text-color="white" class="mr-2 my-1">
          <v-icon x-small left>mdi-calendar-blank</v-icon>
          Term {{ termNo || "—" }}
        </v-chip>
        <v-chip small outlined class="mr-2 my-1">
          <v-icon x-small left>{{
            isStreamTerm ? "mdi-source-branch" : "mdi-format-list-checks"
          }}</v-icon>
          {{ isStreamTerm ? "Stream selection" : "Elective selection" }}
        </v-chip>
        <v-chip small outlined class="my-1">
          <v-icon x-small left>mdi-account-group</v-icon>
          {{ students.length }} student{{ students.length === 1 ? "" : "s" }}
        </v-chip>

        <v-btn
          small
          outlined
          color="primary"
          class="ml-2 my-1"
          :disabled="!commonCourses.length"
          @click="commonCoursesDialog = true"
        >
          <v-icon x-small left>mdi-book-open-variant</v-icon>
          View Courses
        </v-btn>
      </div>
    </v-sheet>

    <searchBar :addField="false" @updateSearch="(v) => (search = v)" />

    <v-row>
      <v-col cols="12">
        <v-sheet outlined>
          <v-data-table
            :headers="headers"
            :search="search"
            :items="tableItems"
            :custom-filter="customFilter"
            :loading="loading"
            fixed-header
            class="secondary row-height-50"
          >
            <template #[`item.courses`]="{ item }">
              <v-chip
                small
                color="primary"
                text-color="white"
                style="cursor: pointer"
                @click="openCoursesDialog(item)"
              >
                <v-icon small left>mdi-book-open-variant</v-icon>
                {{ item._student.courses.length }} course{{
                  item._student.courses.length === 1 ? "" : "s"
                }}
              </v-chip>
            </template>

            <template #[`item.electives`]="{ item }">
              <v-chip
                small
                :color="electiveChipColor(item._student)"
                text-color="white"
                style="cursor: pointer"
                @click="openElectivesDialog(item)"
              >
                <v-icon v-if="isElectiveLocked(item._student)" small left
                  >mdi-lock</v-icon
                >
                <v-icon v-else small left>mdi-pencil-outline</v-icon>
                {{ electiveChipLabel(item._student) }}
              </v-chip>
            </template>

            <template #[`item.action`]="{ item }">
              <v-tooltip top>
                <template #activator="{ on, attrs }">
                  <v-icon
                    small
                    :color="
                      isElectiveLocked(item._student) ? 'error' : 'accent'
                    "
                    v-bind="attrs"
                    v-on="on"
                    @click="toggleLock(item._student)"
                  >
                    {{
                      isElectiveLocked(item._student)
                        ? "mdi-lock"
                        : "mdi-lock-open-variant"
                    }}
                  </v-icon>
                </template>
                <span>{{
                  isElectiveLocked(item._student)
                    ? "Unlock selection"
                    : "Lock selection"
                }}</span>
              </v-tooltip>
            </template>
          </v-data-table>
        </v-sheet>
      </v-col>
    </v-row>

    <v-dialog v-model="commonCoursesDialog" max-width="520">
      <v-card>
        <v-card-title class="font-weight-medium secondary--text primary">
          <span class="text-truncate">Term {{ termNo || "—" }} — Courses</span>
          <v-spacer />
          <v-btn small icon @click="commonCoursesDialog = false">
            <v-icon small color="secondary">mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-4">
          <p class="text-caption text--secondary mb-3">
            Core courses for this term are common to every student in the batch.
          </p>

          <v-list dense class="py-0">
            <div v-for="(course, idx) in commonCourses">
              <v-list-item
                class="px-0"
                :key="course.program_scheme_term_course_id || idx"
              >
                <v-avatar size="26" color="primary" class="mr-3">
                  <span class="white--text text-caption font-weight-bold">{{
                    idx + 1
                  }}</span>
                </v-avatar>
                <v-list-item-content>
                  <v-list-item-title class="font-weight-medium">
                    {{ course.course_name || "Not yet assigned" }}
                  </v-list-item-title>
                  <v-list-item-subtitle v-if="course.course_code">
                    {{ course.course_code }}
                    <span v-if="course.credits">
                      • {{ course.credits }} credits</span
                    >
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
              <v-divider v-if="idx < commonCourses.length - 1" />
            </div>

            <v-list-item v-if="!commonCourses.length" class="px-0">
              <v-list-item-content>
                <v-list-item-title class="text-caption text--secondary">
                  No courses configured for this term.
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="coursesDialog" max-width="520">
      <v-card v-if="dialogStudent">
        <v-card-title class="font-weight-medium secondary--text primary">
          <span class="text-truncate">{{ dialogStudent.name }} — Courses</span>
          <v-spacer />
          <v-btn small icon @click="coursesDialog = false">
            <v-icon small color="secondary">mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-4">
          <p class="text-caption text--secondary mb-3">
            Courses for Term {{ dialogStudent.term_no }}, listed in sequence.
          </p>

          <v-list dense class="py-0">
            <div v-for="(course, idx) in sortedCourses">
              <v-list-item
                class="px-0"
                :key="course.program_scheme_term_course_id || idx"
              >
                <v-avatar size="26" color="primary" class="mr-3">
                  <span class="white--text text-caption font-weight-bold">{{
                    idx + 1
                  }}</span>
                </v-avatar>
                <v-list-item-content>
                  <v-list-item-title class="font-weight-medium">
                    {{ course.course_name || "Not yet assigned" }}
                  </v-list-item-title>
                  <v-list-item-subtitle v-if="course.course_code">
                    {{ course.course_code }}
                    <span v-if="course.credits">
                      • {{ course.credits }} credits</span
                    >
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
              <v-divider v-if="idx < sortedCourses.length - 1" />
            </div>

            <v-list-item v-if="!sortedCourses.length" class="px-0">
              <v-list-item-content>
                <v-list-item-title class="text-caption text--secondary">
                  No courses configured for this term.
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="electivesDialog" max-width="720" persistent>
      <v-card v-if="dialogStudent">
        <v-card-title class="font-weight-medium secondary--text primary">
          <span class="text-truncate">
            {{ dialogStudent.name }} —
            {{ dialogStudent.electives.is_stream ? "Stream" : "Electives" }}
          </span>
          <v-spacer />
          <v-chip
            v-if="isElectiveLocked(dialogStudent)"
            small
            color="amber"
            text-color="white"
            class="mr-2"
          >
            <v-icon small left>mdi-lock</v-icon>
            Locked
          </v-chip>
          <v-btn small icon @click="closeElectivesDialog">
            <v-icon small color="secondary">mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />

        <v-card-text class="pt-5" style="max-height: 64vh; overflow-y: auto">
          <template v-if="dialogStudent.electives.is_stream">
            <p class="text-caption text--secondary mb-4">
              Select exactly one stream for this student. Changing the stream
              replaces the previous selection.
            </p>

            <v-row dense>
              <v-col
                v-for="(stream, idx) in dialogStudent.electives.streams"
                :key="stream.stream_name"
                cols="12"
                sm="6"
              >
                <v-card
                  outlined
                  class="stream-card fill-height"
                  :class="streamCardClass(stream)"
                  :disabled="isStreamCardDisabled(stream)"
                  @click="selectStream(stream)"
                >
                  <div class="d-flex align-center px-3 py-2 stream-card__head">
                    <v-avatar size="24" color="primary" class="mr-2">
                      <span class="white--text text-caption font-weight-bold">{{
                        idx + 1
                      }}</span>
                    </v-avatar>
                    <span class="text-subtitle-2 text-truncate">{{
                      stream.stream_name
                    }}</span>
                    <v-spacer />
                    <v-chip
                      v-if="
                        isSavedStream(stream) && isElectiveLocked(dialogStudent)
                      "
                      x-small
                      color="amber"
                      text-color="white"
                    >
                      <v-icon x-small left>mdi-lock</v-icon> Locked
                    </v-chip>
                    <v-chip
                      v-else-if="isSavedStream(stream)"
                      x-small
                      color="success"
                      text-color="white"
                    >
                      <v-icon x-small left>mdi-check</v-icon> Saved
                    </v-chip>
                    <v-icon
                      v-else-if="isTempStream(stream)"
                      small
                      color="primary"
                      >mdi-check-circle</v-icon
                    >
                    <v-icon v-else small color="grey lighten-1"
                      >mdi-checkbox-blank-circle-outline</v-icon
                    >
                  </div>
                  <v-divider />
                  <div class="px-3 py-2">
                    <div class="text-caption text--secondary mb-1">
                      {{ stream.courses.length }} course{{
                        stream.courses.length === 1 ? "" : "s"
                      }}
                      included
                    </div>
                    <div
                      v-for="course in stream.courses"
                      :key="course.id"
                      class="text-body-2 py-1 d-flex align-center"
                    >
                      <v-icon x-small class="mr-1 text--secondary"
                        >mdi-circle-small</v-icon
                      >
                      <span>
                        <span v-if="course.code" class="text--secondary"
                          >{{ course.code }} - </span
                        >{{ course.name }}
                        <span v-if="course.credits" class="text--secondary"
                          >• {{ course.credits }} credits</span
                        >
                      </span>
                    </div>
                  </div>
                </v-card>
              </v-col>
            </v-row>

            <v-sheet
              outlined
              rounded
              class="mt-4 pa-3 d-flex flex-wrap align-center"
            >
              <div class="mr-auto">
                <div class="text-caption text--secondary">Selected stream</div>
                <div class="text-subtitle-2">
                  {{ tempStreamName || "No stream selected yet" }}
                </div>
              </div>
              <div class="d-flex align-center">
                <v-btn
                  text
                  small
                  :disabled="savingElective"
                  @click="closeElectivesDialog"
                  >Cancel</v-btn
                >
                <v-btn
                  small
                  class="primary white--text ml-2"
                  :loading="savingElective"
                  :disabled="
                    !tempStreamName ||
                    tempStreamName ===
                      dialogStudent.electives.selected_stream_name ||
                    isElectiveLocked(dialogStudent)
                  "
                  @click="saveStreamSelection"
                >
                  {{
                    dialogStudent.electives.selected_stream_name
                      ? "Update Stream"
                      : "Save Stream"
                  }}
                </v-btn>
              </div>
            </v-sheet>
          </template>

          <template v-else>
            <v-card
              v-for="(slot, idx) in dialogStudent.electives.slots"
              :key="slot.program_scheme_term_course_id"
              outlined
              class="mb-3"
            >
              <div class="d-flex align-center px-3 py-2 grey lighten-4">
                <span class="text-subtitle-2">Elective {{ idx + 1 }}</span>
                <v-spacer />
                <v-chip
                  v-if="slot.course_id && !slot.locked"
                  x-small
                  color="success"
                  text-color="white"
                >
                  <v-icon x-small left>mdi-check</v-icon> Configured
                </v-chip>
                <v-chip
                  v-else-if="slot.locked"
                  x-small
                  color="amber"
                  text-color="white"
                >
                  <v-icon x-small left>mdi-lock</v-icon> Locked
                </v-chip>
                <v-chip v-else x-small color="orange" text-color="white">
                  <v-icon x-small left>mdi-clock-outline</v-icon> Pending
                </v-chip>
              </div>
              <v-divider />
              <div class="px-3 py-3">
                <div class="text-caption text--secondary">Selected course</div>
                <div class="text-body-2 font-weight-medium mb-3">
                  {{ selectedCourseLabel(slot) }}
                </div>

                <v-select
                  :value="
                    tempSlotSelections[slot.program_scheme_term_course_id]
                  "
                  :items="slot.available_courses"
                  :item-text="courseLabel"
                  item-value="id"
                  outlined
                  dense
                  hide-details
                  :disabled="slot.locked"
                  placeholder="Select a course"
                  @change="(val) => selectSlotCourse(slot, val)"
                />
              </div>
            </v-card>

            <p
              v-if="!dialogStudent.electives.slots.length"
              class="text-caption text--secondary"
            >
              No elective slots configured for this term.
            </p>

            <v-sheet
              v-else
              outlined
              rounded
              class="mt-4 pa-3 d-flex flex-wrap align-center"
            >
              <div class="mr-auto">
                <div class="text-caption text--secondary">
                  Elective selections
                </div>
                <div class="text-subtitle-2">
                  {{ selectedSlotCount }} of
                  {{ dialogStudent.electives.slots.length }} selected
                </div>
              </div>
              <div class="d-flex align-center">
                <v-btn
                  text
                  small
                  :disabled="savingElective"
                  @click="closeElectivesDialog"
                  >Cancel</v-btn
                >
                <v-btn
                  small
                  class="primary white--text ml-2"
                  :loading="savingElective"
                  :disabled="!hasSlotChanges"
                  @click="saveAllSlotSelections"
                >
                  Save selections
                </v-btn>
              </div>
            </v-sheet>
          </template>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import searchBar from "@/components/searchBar.vue";
import {studentElectiveHeaders} from "@/constants/headers";

export default {
  components: {
    searchBar,
  },
  data() {
    return {
      students: [],
      search: "",
      loading: false,
      coursesDialog: false,
      commonCoursesDialog: false,
      electivesDialog: false,
      dialogStudent: null,
      savingElective: false,
      tempStreamName: null,
      tempSlotSelections: {},
    };
  },
  computed: {
    hasElectiveSelection() {
      // True when at least one student has something to select this term: a
      // stream option or an elective slot. Streams-only or slots-only both count.
      return (this.students || []).some((s) => {
        const e = s && s.electives;
        if (!e) return false;
        return e.is_stream
          ? (e.streams || []).length > 0
          : (e.slots || []).length > 0;
      });
    },

    headers() {
      // Hide the Electives column entirely when there is nothing to select.
      if (this.hasElectiveSelection) return studentElectiveHeaders;
      return studentElectiveHeaders.filter((h) => h.value !== "electives");
    },

    termNo() {
      return (
        this.$route.query.term_no ||
        (this.students[0] && this.students[0].term_no) ||
        null
      );
    },

    isStreamTerm() {
      return !!(
        this.students[0] &&
        this.students[0].electives &&
        this.students[0].electives.is_stream
      );
    },

    tableItems() {
      return this.students.map((student) => ({
        usn_id: student.usn_id,
        name: student.name,
        email: student.email,
        _student: student,
      }));
    },

    electiveSlots() {
      if (!this.dialogStudent || this.dialogStudent.electives.is_stream)
        return [];
      return this.dialogStudent.electives.slots || [];
    },

    selectedSlotCount() {
      return this.electiveSlots.filter(
        (slot) => this.tempSlotSelections[slot.program_scheme_term_course_id],
      ).length;
    },

    slotChanges() {
      // Unlocked slots whose pending selection differs from what's saved.
      return this.electiveSlots.filter((slot) => {
        const val = this.tempSlotSelections[slot.program_scheme_term_course_id];
        return !slot.locked && val && val !== slot.course_id;
      });
    },

    hasSlotChanges() {
      return this.slotChanges.length > 0;
    },

    sortedCourses() {
      if (!this.dialogStudent) return [];
      return (this.dialogStudent.courses || [])
        .slice()
        .sort((a, b) => (a.order_sequence || 0) - (b.order_sequence || 0));
    },

    commonCourses() {
      // Core courses are identical for every student in the term, so the first
      // student's list is representative.
      const first = this.students[0];
      if (!first) return [];
      return (first.courses || [])
        .slice()
        .sort((a, b) => (a.order_sequence || 0) - (b.order_sequence || 0));
    },
  },
  created() {
    this.$store.commit("setBreadcrumbs", [
      { text: "Elective Configuration Old", to: { name: "Elective Configuration Old" } },
      { text: "Electives" },
    ]);
    this.fetchStudentElectives();
  },
  methods: {
    async fetchStudentElectives() {
      this.loading = true;
      const { program_batch_id, partner_id, term_no, batch_scheme_id } =
        this.$route.query;

      try {
        const res = await this.$axios.get(
          "/get_student_electives_with_options_by_program_batch",
          { params: { program_batch_id, partner_id, term_no, batch_scheme_id } },
        );
        this.students = res.data.data || [];
      } catch (error) {
        this.$store.commit("showSnackbar", {
          message:
            error.response?.data?.message ||
            "Failed to fetch student electives",
          color: "error",
        });
      } finally {
        this.loading = false;
      }
    },

    courseLabel(course) {
      if (!course) return "";
      const code = course.code ? `${course.code} - ` : "";
      const credits = course.credits ? ` • ${course.credits} credits` : "";
      return `${code}${course.name}${credits}`;
    },

    selectedCourseLabel(slot) {
      if (!slot.course_id && !slot.course_name) return "No course selected yet";
      const course =
        (slot.available_courses || []).find((c) => c.id === slot.course_id) ||
        {};
      const code = slot.course_code || course.code;
      const name = slot.course_name || course.name;
      const credits = slot.course_credits || course.credits;
      const codePart = code ? `${code} - ` : "";
      const creditPart = credits ? ` • ${credits} credits` : "";
      return `${codePart}${name}${creditPart}`;
    },

    customFilter(value, search, item) {
      if (!search) return true;
      const s = search.toLowerCase();
      return (
        (item.usn_id || "").toLowerCase().includes(s) ||
        (item.name || "").toLowerCase().includes(s) ||
        (item.email || "").toLowerCase().includes(s)
      );
    },


    isElectiveLocked(student) {
      if (student.electives.is_stream) {
        return !!student.electives.locked;
      }
      return (student.electives.slots || []).some((s) => s.locked);
    },

    electiveChipColor(student) {
      if (student.electives.is_stream) {
        return student.electives.selected_stream_name ? "success" : "warning";
      }
      const total = student.electives.slots.length;
      const done = student.electives.slots.filter((s) => !!s.course_id).length;
      if (total === 0) return "grey";
      return done === total ? "success" : "warning";
    },

    electiveChipLabel(student) {
      if (student.electives.is_stream) {
        return student.electives.selected_stream_name || "No stream selected";
      }
      const total = student.electives.slots.length;
      const done = student.electives.slots.filter((s) => !!s.course_id).length;
      return `${done} of ${total} configured`;
    },


    openCoursesDialog(row) {
      this.dialogStudent = row._student;
      this.coursesDialog = true;
    },


    openElectivesDialog(row) {
      this.dialogStudent = row._student;

      if (this.dialogStudent.electives.is_stream) {
        this.tempStreamName =
          this.dialogStudent.electives.selected_stream_name || null;
      } else {
        this.tempSlotSelections = {};
        this.dialogStudent.electives.slots.forEach((slot) => {
          this.$set(
            this.tempSlotSelections,
            slot.program_scheme_term_course_id,
            slot.course_id || null,
          );
        });
      }

      this.electivesDialog = true;
    },

    closeElectivesDialog() {
      this.electivesDialog = false;
      this.dialogStudent = null;
      this.tempStreamName = null;
      this.tempSlotSelections = {};
    },


    isTempStream(stream) {
      return stream.stream_name === this.tempStreamName;
    },

    isSavedStream(stream) {
      return (
        stream.stream_name === this.dialogStudent.electives.selected_stream_name
      );
    },

    isStreamCardDisabled(stream) {
      return (
        this.isElectiveLocked(this.dialogStudent) && !this.isSavedStream(stream)
      );
    },

    streamCardClass(stream) {
      return {
        "stream-card--active": this.isTempStream(stream),
        "stream-card--saved":
          this.isSavedStream(stream) && !this.isTempStream(stream),
      };
    },

    selectStream(stream) {
      if (this.isStreamCardDisabled(stream)) return;
      this.tempStreamName = stream.stream_name;
    },

    async saveStreamSelection() {
      if (!this.tempStreamName) return;

      const stream = this.dialogStudent.electives.streams.find(
        (s) => s.stream_name === this.tempStreamName,
      );
      if (!stream || !stream.courses.length) return;

      this.savingElective = true;
      try {
        const representativeCourse = stream.courses[0];

        const payload = {
          id: this.dialogStudent.electives.student_elective_id,
          student_detail_id: this.dialogStudent.electives.student_detail_id,
          term_no: this.dialogStudent.electives.term_no,
          course_id: representativeCourse.id,
          program_scheme_term_course_id:
            representativeCourse.program_scheme_term_course_id,
          program_batch_id: this.dialogStudent.electives.program_batch_id,
          partner_id: this.dialogStudent.electives.partner_id,
        };

        const res = await this.$axios.put("/admin/student_electives/upsert", {
          student_elective: payload,
        });
        const updated = res.data.data;

        this.dialogStudent.electives.selected_stream_name = this.tempStreamName;
        this.dialogStudent.electives.selected_course_id = updated.course_id;
        this.dialogStudent.electives.student_elective_id = updated.id;

        this.$store.commit("showSnackbar", {
          message: "Stream saved successfully",
          color: "success",
        });
      } catch (error) {
        this.$store.commit("showSnackbar", {
          message:
            error.response?.data?.message || "Failed to save stream selection",
          color: "error",
        });
      } finally {
        this.savingElective = false;
      }
    },


    selectSlotCourse(slot, courseId) {
      this.$set(
        this.tempSlotSelections,
        slot.program_scheme_term_course_id,
        courseId,
      );
    },

    async saveAllSlotSelections() {
      const changed = this.slotChanges;
      if (!changed.length) return;

      this.savingElective = true;
      try {
        // Each slot is a separate upsert (own unique constraint); save
        // sequentially so a failure stops cleanly with the rest untouched.
        for (const slot of changed) {
          const courseId =
            this.tempSlotSelections[slot.program_scheme_term_course_id];

          const payload = {
            id: slot.id,
            student_detail_id: slot.student_detail_id,
            term_no: slot.term_no,
            course_id: courseId,
            program_scheme_term_course_id: slot.program_scheme_term_course_id,
            program_batch_id: slot.program_batch_id,
            partner_id: slot.partner_id,
          };

          const res = await this.$axios.put("/admin/student_electives/upsert", {
            student_elective: payload,
          });
          const updated = res.data.data;

          const selectedCourse = (slot.available_courses || []).find(
            (c) => c.id === courseId,
          );
          slot.id = updated.id;
          slot.course_id = updated.course_id;
          slot.course_name = selectedCourse
            ? selectedCourse.name
            : slot.course_name;
          slot.course_code = selectedCourse
            ? selectedCourse.code
            : slot.course_code;
          slot.course_credits = selectedCourse
            ? selectedCourse.credits
            : slot.course_credits;
        }

        this.$store.commit("showSnackbar", {
          message: "Electives saved successfully",
          color: "success",
        });
      } catch (error) {
        this.$store.commit("showSnackbar", {
          message: error.response?.data?.message || "Failed to save electives",
          color: "error",
        });
      } finally {
        this.savingElective = false;
      }
    },


    async toggleLock(student) {
      try {
        const res = await this.$axios.put(
          "/admin/student_electives/toggle_lock",
          {
            student_detail_id: student.electives.student_detail_id,
            program_batch_id: student.program_batch_id,
            partner_id: student.partner_id,
            term_no: student.term_no,
          },
        );

        const updatedRows = res.data.data || [];
        const newLocked = updatedRows.length
          ? updatedRows[0].locked
          : !this.isElectiveLocked(student);

        if (student.electives.is_stream) {
          student.electives.locked = newLocked;
        } else {
          student.electives.slots.forEach((slot) => {
            slot.locked = newLocked;
          });
        }

        this.$store.commit("showSnackbar", {
          message: newLocked ? "Selection locked" : "Selection unlocked",
          color: "success",
        });
      } catch (error) {
        this.$store.commit("showSnackbar", {
          message:
            error.response?.data?.message || "Failed to update lock state",
          color: "error",
        });
      }
    },
  },
};
</script>

<style scoped>
button {
  outline: none;
}

.stream-card {
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.stream-card__head {
  background-color: rgba(0, 0, 0, 0.02);
}

.stream-card--active {
  border-color: var(--v-primary-base) !important;
  box-shadow: 0 0 0 1px var(--v-primary-base);
}

.stream-card--saved {
  border-color: var(--v-success-base) !important;
}
</style>
